import { useRef, useState } from "react";
import { TKeysOfType } from "../../../../shared/types";
import {
  EDealRequestCommunicationType,
  EDealRequestTimeInterval,
  EDealRequestViewType,
  EReadinessForLowerSellPrice,
} from "../../../../shared/wizard/steps/types";
import { Wizard } from "react-use-wizard";
import getBaseSteps from "./steps/stepsArray/getBaseSteps";
import getDoNotKnowSteps from "./steps/stepsArray/getDoNotKnowPriceSteps";
import getDoNotKnowSellSteps from "./steps/stepsArray/getDoNotKnowPriceSellSteps";
import getDoNotKnowSwapSteps from "./steps/stepsArray/getDoNotKnowPriceSwapSteps";
import getKnowPriceSteps from "./steps/stepsArray/getKnowPriceSteps";
import { IDataFeatures } from "../../../../shared/wizard/steps/featuresStep";
import AnimatedStep from "../../../../shared/wizard/steps/animatedStep";

import clsx from "clsx";
import styles from "./styles.module.scss";

export interface IFillDataWizardSell extends IDataFeatures {
  complexId: number | null;
  streetId: number | null;
  buildingId: number | null;
  areaInSqM: number | null;
  apartmentNumber: number | null;
  facilityTypeId: number | null;
  estimatedPrice: number | null;

  communicationType: EDealRequestCommunicationType | null;
  timeIntervalTypeCommunication: EDealRequestTimeInterval | null;
  offersForClient: EReadinessForLowerSellPrice | null;
  viewRealtorParticipation: EDealRequestViewType | null;

  wishPriceSell: null | number;
}

export type TStringKeysOfIFillDataWizardSell = TKeysOfType<
  IFillDataWizardSell,
  string | null
>;

export type TNumberKeysOfIFillDataWizardSell = TKeysOfType<
  IFillDataWizardSell,
  number | null | number[]
>;

export type TBooleanKeysOfIFillDataWizardSell = TKeysOfType<
  IFillDataWizardSell,
  boolean | null
>;

export type TKeyOfDataWizardSell = keyof IFillDataWizardSell;

const IS_PADDING_NARROW = window.innerWidth < 600;

const INITIAL_FILL_DATA_WIZARD = {
  complexId: null,
  streetId: null,
  buildingId: null,
  areaInSqM: null,
  apartmentNumber: null,
  facilityTypeId: null,
  estimatedPrice: null,

  encumbrance: null,
  childrenOwn: null,
  privatization: null,
  arrest: null,
  debt: null,

  communicationType: null,
  offersForClient: null,
  timeIntervalTypeCommunication: null,
  viewRealtorParticipation: null,

  wishPriceSell: null,
};

// https://sboard.online/boards/8c2808b6-6787-4dd3-bf8e-f6f9666a8374 смотри схему в сборде. Ветвления разложены по папкам.

const SellWizard = () => {
  const [fillDataWizard, setFillDataWizard] = useState<IFillDataWizardSell>(
    INITIAL_FILL_DATA_WIZARD
  );

  const handleChangeDataString = (
    data: string,
    key: TStringKeysOfIFillDataWizardSell
  ) => {
    setFillDataWizard((prev) => {
      return { ...prev, [key]: data };
    });
  };

  const handleChangeDataNumber = (
    data: number | number[],
    key: TNumberKeysOfIFillDataWizardSell
  ) => {
    setFillDataWizard((prev) => {
      return { ...prev, [key]: data };
    });
  };

  const handleChangeDataObject = (data: Partial<IFillDataWizardSell>) => {
    setFillDataWizard((prev) => {
      return { ...prev, ...data };
    });
  };

  const handleResetData = (keys: TKeyOfDataWizardSell[], data?: number[]) => {
    if (keys.length === 1) {
      const key = keys[0];
      setFillDataWizard((prev) => {
        return { ...prev, [key]: data ? data : null };
      });
    } else {
      const resetValues = keys.reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, {} as Partial<IFillDataWizardSell>);
      setFillDataWizard((prevData) => ({ ...prevData, ...resetValues }));
    }
  };

  const isNextStep_estimateWithoutApartment =
    fillDataWizard.areaInSqM &&
    fillDataWizard.buildingId &&
    fillDataWizard.streetId;

  const isNextStep_estimate = !Boolean(
    isNextStep_estimateWithoutApartment && fillDataWizard.apartmentNumber
  );
  const isNextStep_communicationType = !Boolean(
    fillDataWizard.communicationType
  );
  const isNextStep_timeIntervalTypeCommunication = !Boolean(
    fillDataWizard.timeIntervalTypeCommunication
  );
  const isNextStep_viewRealtorParticipation = !Boolean(
    fillDataWizard.viewRealtorParticipation
  );

  const dataFeatures: IDataFeatures = {
    encumbrance: fillDataWizard.encumbrance,
    childrenOwn: fillDataWizard.childrenOwn,
    privatization: fillDataWizard.privatization,
    arrest: fillDataWizard.arrest,
    debt: fillDataWizard.debt,
  };

  const dataFormEstimate = {
    streetId: fillDataWizard.streetId,
    buildingId: fillDataWizard.buildingId,
    areaInSqM: fillDataWizard.areaInSqM,
    apartmentNumber: fillDataWizard.apartmentNumber,
  };
  const isNextStep_dataFeatures = Object.values(dataFeatures).some(
    (value) => value === null
  );

  const isNextStep_offersForClient = !Boolean(fillDataWizard.offersForClient);

  const isFourStepPrev = !Boolean(fillDataWizard.wishPriceSell);

  const base = getBaseSteps();

  const doNotKnowPriceSteps = getDoNotKnowSteps(
    dataFormEstimate,
    dataFeatures,
    handleResetData,
    handleChangeDataNumber,
    handleChangeDataObject,
    {
      isNextStep_estimate,
      isNextStep_dataFeatures,
    }
  );

  const sellDoNotKnowPriceSteps = getDoNotKnowSellSteps(
    fillDataWizard,
    handleResetData,
    handleChangeDataString,
    {
      isNextStep_offersForClient,
      isNextStep_communicationType,
      isNextStep_viewRealtorParticipation,
      isNextStep_timeIntervalTypeCommunication,
    }
  );

  const swapDoNotKnowPriceSteps = getDoNotKnowSwapSteps(
    fillDataWizard,
    handleResetData,
    handleChangeDataString,
    {
      isNextStep_offersForClient,
      isNextStep_communicationType,
      isNextStep_viewRealtorParticipation,
      isNextStep_timeIntervalTypeCommunication,
    }
  );

  const knowSteps = getKnowPriceSteps(
    fillDataWizard,
    dataFormEstimate,
    dataFeatures,
    handleResetData,
    handleChangeDataString,
    handleChangeDataNumber,
    handleChangeDataObject,
    {
      isNextStep_estimate,
      isNextStep_dataFeatures,
      isNextStep_offersForClient,
      isNextStep_communicationType,
      isNextStep_viewRealtorParticipation,
      isNextStep_timeIntervalTypeCommunication,
      isFourStepPrev,
    }
  );

  const allSteps = [
    ...base,
    ...doNotKnowPriceSteps,
    ...sellDoNotKnowPriceSteps,
    ...swapDoNotKnowPriceSteps,
    ...knowSteps,
  ];

  const previousStep = useRef<number>(0);

  return (
    <Wizard
      wrapper={
        <div className={styles.sellWizard}>
          <div className={styles.sellWizard__container}></div>
        </div>
      }
    >
      {allSteps.map((step, index) => {
        return (
          <AnimatedStep key={index} previousStep={previousStep}>
            <div
              className={clsx(
                styles.sellWizard__card,
                IS_PADDING_NARROW && styles.sellWizard__card_paddingNarrow
              )}
            >
              <div className={styles.sellWizard__title}>Продать жилье</div>
              <div className={styles.sellWizard__wizard}>{step}</div>
            </div>
          </AnimatedStep>
        );
      })}
    </Wizard>
  );
};

export default SellWizard;
